import React, { type ReactNode, useId, useState } from 'react';
import { Button, Icon } from '@noths/polaris-design-system-core-components';
import Link from 'next/link';
import { useRouter } from 'next/router';

import type { UIFacet, UIFacetOption } from 'src/components/types/UIFacet';
import {
  DELIVERY_DATE_CHIP_COUNT,
  getDeliveryDateTitle,
  splitDeliveryDateTitle,
} from 'src/utils/deliveryChipDates';
import type { FacetOptionsProps } from '../types';
import {
  ARRIVE_BY_ANSWER,
  ARRIVE_BY_QUESTION,
  ARRIVE_ON_OR_BEFORE,
  SHOW_FEWER_DATES,
  SHOW_MORE_DATES,
  YOUR_DATE,
} from './copy';
import * as styles from './DeliveryDateStrip.styles';

/** The dates shown before "Show more dates". */
export const DELIVERY_DATES_SHOWN_COLLAPSED = 5;

const getDays = (value: string): number | undefined => {
  const days = Number(value.match(/^-(\d+)$/)?.[1]);

  return days > 0 ? days : undefined;
};

/** The date an option stands for in the chips' format, which for an event is its deadline. */
const getDateTitle = (option: UIFacetOption): string | undefined => {
  if (splitDeliveryDateTitle(option.title)) {
    return option.title;
  }

  const days = getDays(option.value);

  return days === undefined ? undefined : getDeliveryDateTitle(days);
};

/** "Estimated to arrive on or before Sat 3rd" once a date is chosen, for a closed facet's header. */
export const getDeliveryDateSummary = (facet: UIFacet): string | undefined => {
  const activeOption = facet.options.find((option) => option.active);
  const chosenDateTitle = activeOption && getDateTitle(activeOption);

  return chosenDateTitle && `${ARRIVE_ON_OR_BEFORE} ${chosenDateTitle}`;
};

interface ChipAttributes {
  'aria-current'?: 'true';
  'aria-label': string;
  'data-in-range'?: 'true';
}

interface NavigatingButtonProps {
  attributes: ChipAttributes;
  children: ReactNode;
  href: string;
  testId: string;
}

const NavigatingButton = ({ attributes, children, href, testId }: NavigatingButtonProps) => {
  const router = useRouter();

  return (
    <button
      {...attributes}
      css={styles.chip}
      data-href={href}
      data-testid={testId}
      onClick={() => router.push(href, undefined, { scroll: false, shallow: true })}
      type="button"
    >
      {children}
    </button>
  );
};

interface DeliveryDateChipProps {
  facetKey: string;
  inRange: boolean;
  option: UIFacetOption;
  title: string;
}

const DeliveryDateChip = ({ facetKey, inRange, option, title }: DeliveryDateChipProps) => {
  const date = splitDeliveryDateTitle(title);
  const attributes: ChipAttributes = {
    'aria-current': option.active ? 'true' : undefined,
    'aria-label': title,
    'data-in-range': inRange ? 'true' : undefined,
  };
  const content = date ? (
    <>
      <span css={styles.weekday}>{date.weekday}</span>
      <span css={styles.day}>{date.day}</span>
    </>
  ) : (
    <span css={styles.day}>{title}</span>
  );

  if (!option.href) {
    return (
      <button {...attributes} css={styles.chip} disabled type="button">
        {content}
      </button>
    );
  }

  if (option.crawlable) {
    return (
      <Link
        {...attributes}
        css={styles.chip}
        data-testid={`${facetKey}-${option.value}-link`}
        href={option.href}
        prefetch={false}
        scroll={false}
        shallow
      >
        {content}
      </Link>
    );
  }

  return (
    <NavigatingButton
      attributes={attributes}
      href={option.href}
      testId={`${facetKey}-${option.value}-button`}
    >
      {content}
    </NavigatingButton>
  );
};

/**
 * The delivery facet's options as date chips. Every date before the chosen one is
 * highlighted too, because the filter means "on or before". A date with no products
 * stays in place, disabled, so the dates either side keep their order. A delivery event
 * sits above the dates and is never collapsed away. An event within the dates has taken
 * over its date's option, so that option is drawn in both places and both show as chosen.
 */
export const DeliveryDateStrip = ({ facet }: FacetOptionsProps) => {
  const dateOptions = facet.options.filter((option) => {
    const days = getDays(option.value);

    return days !== undefined && days <= DELIVERY_DATE_CHIP_COUNT;
  });
  const eventOptions = facet.options.filter((option) => !splitDeliveryDateTitle(option.title));
  const activeOption = facet.options.find((option) => option.active);
  const activeDays = activeOption && getDays(activeOption.value);
  const activeDateIndex = dateOptions.findIndex((option) => option.active);
  const [expanded, setExpanded] = useState(activeDateIndex >= DELIVERY_DATES_SHOWN_COLLAPSED);
  const [explained, setExplained] = useState(false);
  const answerId = useId();
  const visibleDateOptions = expanded
    ? dateOptions
    : dateOptions.slice(0, DELIVERY_DATES_SHOWN_COLLAPSED);
  const chosenDateTitle = activeOption && getDateTitle(activeOption);

  const isInRange = (option: UIFacetOption) => {
    const days = getDays(option.value);

    return !option.active && days !== undefined && activeDays !== undefined && days < activeDays;
  };

  return (
    <div css={styles.container}>
      <p css={styles.subtitle} data-chosen={chosenDateTitle ? 'true' : undefined}>
        {`${ARRIVE_ON_OR_BEFORE} ${chosenDateTitle || YOUR_DATE}`}
      </p>
      <ul aria-label={facet.title} css={styles.grid}>
        {eventOptions.map((option) => (
          <li css={styles.wideItem} key={option.value}>
            <DeliveryDateChip
              facetKey={facet.key}
              inRange={false}
              option={option}
              title={option.title}
            />
          </li>
        ))}
        {visibleDateOptions.map((option) => (
          <li key={option.value}>
            <DeliveryDateChip
              facetKey={facet.key}
              inRange={isInRange(option)}
              option={option}
              title={getDateTitle(option) ?? option.title}
            />
          </li>
        ))}
      </ul>
      {dateOptions.length > DELIVERY_DATES_SHOWN_COLLAPSED && (
        <Button
          aria-expanded={expanded}
          compact
          fullWidth
          iconLeft={expanded ? 'expanded' : 'collapsed'}
          onClick={() => setExpanded(!expanded)}
          secondary
          type="button"
        >
          {expanded ? SHOW_FEWER_DATES : SHOW_MORE_DATES}
        </Button>
      )}
      {/* A button rather than details and summary, because the facet accordion is a details
          whose styles rotate every summary > span inside it. */}
      <div css={styles.explainer}>
        <button
          aria-controls={answerId}
          aria-expanded={explained}
          css={styles.explainerQuestion}
          onClick={() => setExplained(!explained)}
          type="button"
        >
          <Icon aria-hidden name="info" size="sm" />
          <span css={styles.explainerQuestionText}>{ARRIVE_BY_QUESTION}</span>
        </button>
        {explained && (
          <p css={styles.explainerAnswer} id={answerId}>
            {ARRIVE_BY_ANSWER}
          </p>
        )}
      </div>
    </div>
  );
};
